import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, LifeBuoy, LogOut, MapPin, Menu, Search, Sparkles, Ticket, User, X } from 'lucide-react';
import { useEvents } from '@/context/EventContext';
import { useAuth } from '@/context/AuthContext';
import { useSubcategories } from '@/hooks/useCatalog';
import { CategoryIcon } from '@/lib/categoryIcons';
import { subcategoryIcon } from '@/lib/subcategoryIcons';
import LocationModal from './LocationModal';
import { useCity } from '@/hooks/useCatalog';
import LKLogo from '@/assets/images/LK.png';
import { resolveLink, useMenu } from '@/lib/siteContent';
import { useSupportUnread } from '@/lib/support';

// Fallback until the admin configures the web_header menu.
const DEFAULT_NAV_ITEMS = [
  { name: 'Events', to: '/', hasMenu: true },
  { name: 'Offers', to: '/offers' },
  { name: 'My Bookings', to: '/my-bookings' },
  { name: 'Kuicko it', href: 'https://www.kuicko.in/' },
  { name: 'About Us', href: 'https://localkonnect.com/about.html' },
];

/** Admin menu items (deeplink grammar) → this header's nav item shape. */
function menuToNav(items) {
  return items
    .map((item) => {
      const target = resolveLink(item.link);
      if (!target) return null;
      return target.to ? { name: item.label, to: target.to, hasMenu: !!target.events } : { name: item.label, href: target.href };
    })
    .filter(Boolean);
}

const Header = () => {
  const adminMenu = useMenu('web_header', null);
  const NAV_ITEMS = adminMenu ? menuToNav(adminMenu) : DEFAULT_NAV_ITEMS;
  const { selectedLocation, searchQuery, setSearchQuery } = useEvents();
  // The effective city — a first visit shows the default served city, not a blank label.
  const { city } = useCity();
  const cityLabel = city?.name || selectedLocation;
  const { openAuthModal, user, logout } = useAuth();
  const supportUnread = useSupportUnread(user);
  const { browsable } = useSubcategories();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const profileRef = useRef(null);
  const browseRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) setProfileOpen(false);
      if (browseRef.current && !browseRef.current.contains(event.target)) setBrowseOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // The header sits on top of full-bleed hero art, so it only earns its border
  // and shadow once the page has actually scrolled under it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Route changes should never leave a panel hanging open.
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
    setBrowseOpen(false);
  }, [pathname]);

  // A slide-over that scrolls the page behind it reads as broken.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const submitSearch = (event) => {
    event.preventDefault();
    if (pathname !== '/') navigate('/');
    // Give the grid a frame to mount before scrolling to it.
    requestAnimationFrame(() => {
      document.getElementById('all-events')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleProfile = () => (user ? setProfileOpen((o) => !o) : openAuthModal());

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    navigate('/');
  };

  // Browse pages get District's mobile pattern: the logo row scrolls away and
  // a pinned bar keeps search + the category rail in reach. Funnel pages
  // (event, tickets, seats, checkout) keep the whole header pinned instead —
  // search is noise there, and they already carry a sticky bottom CTA.
  const browseMode = pathname === '/' || pathname.startsWith('/events/');

  // The header only earns an edge once content scrolls under it — and on
  // mobile browse pages it scrolls away entirely, so the edge is desktop-only.
  const headerEdge = !scrolled
    ? 'border-transparent'
    : browseMode
      ? 'border-transparent md:border-ink-200/70 md:shadow-card'
      : 'border-ink-200/70 shadow-card';

  const isActive = (item) => {
    if (!item.to) return false;
    if (item.to === '/') return pathname === '/' || pathname.startsWith('/events/');
    return pathname.startsWith(item.to);
  };

  const searchField = (id, className) => (
    <div className={`relative ${className}`}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
      {/* 16px on phones: iOS Safari zooms the whole page when an input under
          16px takes focus, and never zooms back out. */}
      <input
        id={id}
        type="search"
        enterKeyHint="search"
        aria-label="Search events"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search events, artists, venues…"
        className="lk-focus w-full rounded-full border border-ink-200 bg-surface-muted py-3 pl-10 pr-11 text-base outline-none transition-colors focus:border-brand-secondary focus:bg-white md:py-2.5 md:text-sm [&::-webkit-search-cancel-button]:hidden"
      />
      {searchQuery && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => setSearchQuery('')}
          className="lk-focus absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-ink-400 hover:text-ink-900"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  // Skip link target: every page renders exactly one <main>, so focus it
  // rather than relying on each page to carry a matching id.
  const skipToContent = (e) => {
    const main = document.querySelector('main');
    if (!main) return;
    e.preventDefault();
    main.setAttribute('tabindex', '-1');
    main.focus({ preventScroll: false });
    main.scrollIntoView();
  };

  return (
    <>
      <a href="#main" onClick={skipToContent} className="lk-skip-link">
        Skip to content
      </a>
      <header
        className={`top-0 z-50 w-full border-b bg-white/90 backdrop-blur transition-shadow duration-200 ${
          browseMode ? 'relative md:sticky' : 'sticky'
        } ${headerEdge}`}
      >
        <div className="lk-container">
          <div className="flex h-14 items-center gap-3 md:h-[72px] md:gap-4">
            {/* Logo + location */}
            <div className="flex min-w-0 shrink items-center gap-2 md:shrink-0 md:gap-3 lg:gap-6">
              <Link to="/" className="lk-focus flex shrink-0 items-center rounded-lg">
                <img src={LKLogo} alt="LocalKonnect" className="h-8 w-auto md:h-11" />
              </Link>

              {/* Phones: the city is how people orient on a ticketing site, so it
                  stays visible rather than living only in the slide-over. */}
              <button
                type="button"
                onClick={() => setLocationOpen(true)}
                aria-label={`City: ${cityLabel}. Change`}
                className="lk-focus flex min-h-[44px] min-w-0 items-center gap-1 rounded-full px-2 text-sm font-semibold text-ink-900 md:hidden"
              >
                <MapPin className="h-4 w-4 flex-none text-brand-secondary" aria-hidden="true" />
                <span className="truncate">{cityLabel}</span>
                <ChevronDown className="h-3.5 w-3.5 flex-none text-ink-400" aria-hidden="true" />
              </button>

              <button
                type="button"
                onClick={() => setLocationOpen(true)}
                className="lk-focus hidden items-center gap-2 rounded-full border border-transparent px-2 py-1.5 transition-colors hover:border-ink-200 hover:bg-surface-muted md:flex"
              >
                <MapPin className="h-4 w-4 text-brand-secondary" />
                <span className="flex flex-col items-start leading-none">
                  <span className="lk-eyebrow">Location</span>
                  <span className="mt-0.5 flex items-center gap-1 text-sm font-semibold text-ink-900">
                    {cityLabel}
                    <ChevronDown className="h-3.5 w-3.5 text-ink-400" />
                  </span>
                </span>
              </button>
            </div>

            {/* Desktop nav */}
            <nav className="hidden items-center gap-0.5 lg:flex">
              {NAV_ITEMS.map((item) => {
                const active = isActive(item);

                if (item.hasMenu && browsable.length > 0) {
                  return (
                    <div key={item.name} className="relative" ref={browseRef}>
                      <button
                        type="button"
                        onClick={() => setBrowseOpen((o) => !o)}
                        aria-expanded={browseOpen}
                        className={`lk-focus flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                          active
                            ? 'bg-brand-tint text-brand-secondary'
                            : 'text-ink-500 hover:bg-surface-muted hover:text-ink-900'
                        }`}
                      >
                        {item.name}
                        <ChevronDown
                          className={`h-3.5 w-3.5 transition-transform ${browseOpen ? 'rotate-180' : ''}`}
                        />
                      </button>

                      <AnimatePresence>
                        {browseOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.12 }}
                            className="absolute left-0 z-50 mt-2 w-[420px] overflow-hidden rounded-2xl border border-ink-200 bg-white p-2 shadow-cardHover"
                          >
                            <Link
                              to="/"
                              className="flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-semibold text-ink-900 hover:bg-surface-muted"
                            >
                              All events
                              <span className="lk-eyebrow">Everything on</span>
                            </Link>
                            <div className="my-1 h-px bg-ink-200/70" />
                            <div className="grid grid-cols-2 gap-1">
                              {browsable.map((sub) => (
                                <Link
                                  key={sub.id}
                                  to={`/events/${sub.slug}`}
                                  className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-ink-700 transition-colors hover:bg-surface-muted"
                                >
                                  <span
                                    className="flex h-8 w-8 flex-none items-center justify-center rounded-lg"
                                    style={{ backgroundColor: sub.backgroundColor || '#f2f4fb' }}
                                  >
                                    <CategoryIcon
                                      src={subcategoryIcon(sub)}
                                      name={sub.name}
                                      className="h-5 w-5"
                                    />
                                  </span>
                                  <span className="min-w-0 flex-1 truncate font-medium">{sub.name}</span>
                                  {sub.eventCount > 0 && (
                                    <span className="tabular text-xs text-ink-400">{sub.eventCount}</span>
                                  )}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return item.to ? (
                  <Link
                    key={item.name}
                    to={item.to}
                    className={`lk-focus rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      active
                        ? 'bg-brand-tint text-brand-secondary'
                        : 'text-ink-500 hover:bg-surface-muted hover:text-ink-900'
                    }`}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lk-focus rounded-full px-4 py-2 text-sm font-medium text-ink-500 transition-colors hover:bg-surface-muted hover:text-ink-900"
                  >
                    {item.name}
                  </a>
                );
              })}
            </nav>

            {/* Search takes the slack, so it grows with the window instead of
                hiding behind an icon on a page that is entirely about finding
                something. */}
            <form onSubmit={submitSearch} className="ml-auto hidden min-w-0 flex-1 justify-end md:flex">
              {searchField('site-search', 'w-full max-w-xs')}
            </form>

            {/* Actions */}
            <div className="ml-auto flex shrink-0 items-center gap-2 md:ml-0">
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={handleProfile}
                  aria-label={user ? 'Account menu' : 'Log in'}
                  className="lk-focus relative flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary text-sm font-bold text-white shadow-card transition-colors hover:bg-brand-accent"
                >
                  {user ? (user.name ? user.name[0].toUpperCase() : 'U') : <User className="h-5 w-5" />}
                  {user && supportUnread > 0 && (
                    <span className="absolute right-0 top-0 h-3 w-3 rounded-full bg-brand-secondary ring-2 ring-white" aria-hidden="true" />
                  )}
                </button>

                <AnimatePresence>
                  {profileOpen && user && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.96, y: 8 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96, y: 8 }}
                      transition={{ duration: 0.12 }}
                      className="absolute right-0 z-50 mt-2 w-60 origin-top-right overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-cardHover"
                    >
                      <div className="border-b border-ink-200/70 bg-surface-muted px-4 py-3">
                        <p className="truncate text-sm font-semibold text-ink-900">
                          {user.name || `+91-${user.phone}`}
                        </p>
                        <p className="truncate text-xs text-ink-500">{user.email || 'Welcome back!'}</p>
                      </div>
                      <div className="p-1.5">
                        <Link
                          to="/my-bookings"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-ink-700 transition-colors hover:bg-surface-muted"
                        >
                          <Ticket className="h-4 w-4" />
                          My Bookings
                        </Link>
                        <Link
                          to="/support"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-ink-700 transition-colors hover:bg-surface-muted"
                        >
                          <LifeBuoy className="h-4 w-4" />
                          Help &amp; support
                          {supportUnread > 0 && (
                            <span className="ml-auto min-w-[1.25rem] rounded-full bg-brand-secondary px-1.5 text-center text-[11px] font-bold leading-5 text-white" aria-label={`${supportUnread} unread replies`}>
                              {supportUnread}
                            </span>
                          )}
                        </Link>
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-danger transition-colors hover:bg-danger-tint"
                        >
                          <LogOut className="h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                className="lk-focus flex h-11 w-11 items-center justify-center rounded-full text-ink-700 hover:bg-surface-muted lg:hidden"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Phones, browse pages: search + category rail stay pinned while the
          logo row above scrolls away. A sibling of <header>, not a child —
          `sticky` only holds within its parent's box. */}
      {browseMode && (
        <div
          className={`sticky top-0 z-40 border-b bg-white/95 backdrop-blur transition-shadow duration-200 md:hidden ${
            scrolled ? 'border-ink-200/70 shadow-card' : 'border-transparent'
          }`}
        >
          <form role="search" onSubmit={submitSearch} className="lk-container pb-2 pt-1">
            {searchField('mobile-browse-search', 'w-full')}
          </form>

          {browsable.length > 0 && (
            <nav
              aria-label="Browse events by category"
              className="scrollbar-hide lk-fade-r flex snap-x gap-1 overflow-x-auto px-3 pb-2"
            >
              {[{ id: 'all', slug: null, name: 'All' }, ...browsable].map((sub) => {
                const to = sub.slug ? `/events/${sub.slug}` : '/';
                const active = sub.slug ? pathname === to : pathname === '/';
                return (
                  <Link
                    key={sub.id}
                    to={to}
                    aria-current={active ? 'page' : undefined}
                    className={`lk-focus flex min-h-[56px] min-w-[64px] shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-2xl px-3 py-1.5 transition-colors active:scale-95 ${
                      active ? 'bg-brand-tint text-brand-secondary' : 'text-ink-700'
                    }`}
                  >
                    {sub.slug ? (
                      <CategoryIcon src={subcategoryIcon(sub)} name={sub.name} className="h-6 w-6" />
                    ) : (
                      <Sparkles className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
                    )}
                    <span className="whitespace-nowrap text-xs font-semibold">{sub.name}</span>
                  </Link>
                );
              })}
            </nav>
          )}
        </div>
      )}

      {/* Mobile slide-over. A full panel rather than an expanding accordion, so
          a long list of browse buckets stays reachable without pushing the
          page down. */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="fixed inset-0 z-[60] bg-black/40 lg:hidden"
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.22 }}
              className="fixed inset-y-0 right-0 z-[70] flex w-[86%] max-w-sm flex-col bg-white shadow-2xl lg:hidden"
            >
              <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-ink-200/70 px-4">
                <img src={LKLogo} alt="LocalKonnect" className="h-9 w-auto" />
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                  className="lk-focus rounded-full p-2 text-ink-700 hover:bg-surface-muted"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                <form
                  onSubmit={(e) => {
                    submitSearch(e);
                    setMenuOpen(false);
                  }}
                  className="mb-4"
                >
                  {searchField('mobile-search', 'w-full')}
                </form>

                <button
                  type="button"
                  onClick={() => {
                    setLocationOpen(true);
                    setMenuOpen(false);
                  }}
                  className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-ink-200 bg-surface-muted px-4 py-3 text-left"
                >
                  <MapPin className="h-5 w-5 flex-none text-brand-secondary" />
                  <span className="min-w-0 flex-1">
                    <span className="lk-eyebrow block">Location</span>
                    <span className="block truncate text-sm font-semibold text-ink-900">
                      {cityLabel}
                    </span>
                  </span>
                  <ChevronDown className="h-4 w-4 flex-none text-ink-400" />
                </button>

                {browsable.length > 0 && (
                  <div className="mb-4">
                    <p className="lk-eyebrow mb-2 px-1">Browse</p>
                    <div className="grid grid-cols-2 gap-2">
                      {browsable.map((sub) => (
                        <Link
                          key={sub.id}
                          to={`/events/${sub.slug}`}
                          className="flex flex-col items-center gap-2 rounded-2xl border border-ink-200/70 p-3 text-center"
                          style={{
                            background: `linear-gradient(180deg,#fff 0%, ${
                              sub.backgroundColor || '#f2f4fb'
                            } 200%)`,
                          }}
                        >
                          <CategoryIcon src={subcategoryIcon(sub)} name={sub.name} className="h-10 w-10" />
                          <span className="text-xs font-bold uppercase tracking-wide text-ink-900">
                            {sub.name}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  {NAV_ITEMS.map((item) =>
                    item.to ? (
                      <Link
                        key={item.name}
                        to={item.to}
                        className={`block rounded-xl px-4 py-3 text-base font-medium ${
                          isActive(item)
                            ? 'bg-brand-tint text-brand-secondary'
                            : 'text-ink-700 hover:bg-surface-muted'
                        }`}
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block rounded-xl px-4 py-3 text-base font-medium text-ink-700 hover:bg-surface-muted"
                      >
                        {item.name}
                      </a>
                    )
                  )}
                  {user && (
                    <Link
                      to="/support"
                      className={`flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium ${
                        pathname.startsWith('/support') ? 'bg-brand-tint text-brand-secondary' : 'text-ink-700 hover:bg-surface-muted'
                      }`}
                    >
                      Help &amp; support
                      {supportUnread > 0 && (
                        <span className="ml-auto min-w-[1.5rem] rounded-full bg-brand-secondary px-1.5 text-center text-xs font-bold leading-6 text-white">{supportUnread}</span>
                      )}
                    </Link>
                  )}
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <LocationModal
        isOpen={locationOpen}
        onClose={() => setLocationOpen(false)}
      />
    </>
  );
};

export default Header;
